import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { GroupForm } from '@/features/community/GroupForm';
import { useChapters, useDeleteGroup, useGroups } from '@/queries/community';
import { useApiError } from '@/lib/useApiError';
import { useUiStore } from '@/stores/ui';
import type { Group } from '@/schemas/admin/community/groups';

export const Route = createFileRoute('/_authed/community/groups')({
  component: GroupsScreen,
});

function GroupsScreen() {
  const { t } = useTranslation('common');
  const locale = useUiStore((s) => s.locale);
  const list = useGroups();
  const chapters = useChapters();
  const del = useDeleteGroup();
  const resolveError = useApiError();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Group | null>(null);
  const [deleting, setDeleting] = React.useState<Group | null>(null);

  const chapterById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of chapters.data ?? []) {
      map.set(c.id, c.name_en ?? c.city_en ?? c.slug);
    }
    return map;
  }, [chapters.data]);

  function displayName(g: Group): string {
    if (locale === 'eg-ar') return g.name_eg_ar ?? g.name_en ?? g.slug;
    if (locale === 'ar') return g.name_ar ?? g.name_en ?? g.slug;
    return g.name_en ?? g.slug;
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(t('actions.delete', { defaultValue: 'Deleted' }));
      setDeleting(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<Group>[] = [
    {
      id: 'name',
      header: 'Group',
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{displayName(r)}</span>
          <span dir="ltr" className="font-mono text-[11px] text-[color:var(--color-fg-muted)]">
            {r.slug}
          </span>
        </div>
      ),
      sortBy: (r) => displayName(r),
    },
    {
      id: 'kind',
      header: 'Kind',
      cell: (r) => <Badge tone="neutral">{r.kind}</Badge>,
      sortBy: (r) => r.kind,
    },
    {
      id: 'chapter',
      header: 'Chapter',
      cell: (r) =>
        r.chapter_id ? (
          (chapterById.get(r.chapter_id) ?? r.chapter_id.slice(0, 8) + '…')
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
    },
    {
      id: 'members',
      header: 'Members',
      cell: (r) =>
        typeof r.member_count === 'number' ? (
          <span dir="ltr" className="font-mono">
            {r.member_count}
          </span>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
      align: 'end',
      sortBy: (r) => r.member_count ?? -1,
    },
    {
      id: 'state',
      header: 'State',
      cell: (r) =>
        r.archived ? (
          <Badge tone="warn">Archived</Badge>
        ) : (
          <Badge tone="success">Active</Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label={`Edit ${displayName(r)}`}>
            <Pencil aria-hidden className="size-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} aria-label={`Delete ${displayName(r)}`}>
            <Trash2 aria-hidden className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Groups</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">
            Interest groups, chapter groups, support spaces, and private rooms. Public groups are surfaced on the consumer Community page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            New group
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <DataTable<Group>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: 'No groups yet.', body: 'Add a group to get started.' }}
            initialSort={{ id: 'name', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New group</DialogTitle>
            <DialogDescription>
              Slug is immutable once saved. Use lowercase letters and hyphens.
            </DialogDescription>
          </DialogHeader>
          <GroupForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit group</DialogTitle>
            <DialogDescription>Update the group name, kind, chapter, or archive state.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <GroupForm group={editing} onSaved={() => setEditing(null)} onCancel={() => setEditing(null)} />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete group?"
        description={deleting ? `Permanently remove "${displayName(deleting)}". Member posts will be preserved as orphaned.` : ''}
        destructive
        requireType="DELETE"
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
